/**
 * eventDatetime.ts — Timezone-aware conversion + display helpers for events.
 *
 * ─── Invariants ──────────────────────────────────────────────────────────────
 *
 * Every `breakfast_events` row now has three time columns:
 *
 *    starts_at         TIMESTAMPTZ   — absolute instant (source of truth)
 *    ends_at           TIMESTAMPTZ   — absolute instant (nullable)
 *    event_timezone    TEXT          — IANA zone the host authored in
 *
 * Two legacy columns — `event_date` (DATE) and `event_time` (TIME) — remain as
 * convenience mirrors of the host-local wall clock. They are NOT authoritative
 * and must never be used to render a time to a *viewer* in another zone.
 *
 * ─── Why raw `Date` is not enough ───────────────────────────────────────────
 *
 * JavaScript's `Date` object is a UTC instant paired with the **browser's**
 * local timezone. That's fine for reading UTC back out, but useless for
 * rendering a stored event in its host-authored timezone on a browser that
 * happens to be in a different one. This module uses `Intl.DateTimeFormat`
 * with an explicit `timeZone` option for all display work and a
 * two-pass offset lookup for the wall-clock → UTC direction (DST-safe).
 *
 * ─── No dependencies ────────────────────────────────────────────────────────
 *
 * Intentionally native-only. `date-fns-tz` would work but adds ~20 KB
 * gzipped; the two helpers we actually need fit in <50 lines.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/**
 * Returns the viewer's current IANA timezone, e.g. `"America/New_York"`.
 * Falls back to `"UTC"` if the runtime doesn't expose it (very old browsers).
 */
export function getLocalTimezone(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz || "UTC";
  } catch {
    return "UTC";
  }
}

/**
 * Returns `true` if two IANA zones represent the same offset schedule.
 * Cheap string check first, fallback to offset-at-now comparison so that
 * pairs like `"Asia/Kolkata"` vs `"Asia/Calcutta"` (deprecated alias) are
 * treated as identical for display purposes.
 */
export function isSameTz(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false;
  if (a === b) return true;
  try {
    const now = Date.now();
    return getTzOffsetMs(now, a) === getTzOffsetMs(now, b);
  } catch {
    return false;
  }
}

// ─── Wall-clock → UTC instant ────────────────────────────────────────────────

/**
 * Given a local wall-clock (`YYYY-MM-DD` + `HH:MM[:SS]`) and the zone that
 * wall clock belongs to, return the matching absolute UTC instant as an
 * ISO-8601 string ending in `Z`.
 *
 * This is the function the event creation/update form calls before sending
 * `starts_at` / `ends_at` to Supabase. Always pass the zone the *host* is
 * authoring in — usually the viewer's own zone at creation time.
 *
 * DST behaviour: the two-pass offset lookup handles the spring-forward /
 * fall-back cases. For times that fall inside the skipped hour (spring
 * forward), the result lands on the nearest real instant; for times inside
 * the repeated hour (fall back), the result picks the first occurrence —
 * matching the behaviour of `date-fns-tz`.
 *
 * @example
 *   zonedWallToUtc("2026-04-21", "09:00", "America/New_York")
 *   // → "2026-04-21T13:00:00.000Z"  (EDT is UTC-4 in late April)
 */
export function zonedWallToUtc(
  dateYmd: string,
  timeHms: string,
  tz: string,
): string {
  const [Y, M, D] = dateYmd.split("-").map(Number);
  const [h, m, s = 0] = timeHms.split(":").map(Number);

  // Treat the wall clock as if it were UTC to build an initial candidate.
  const candidateMs = Date.UTC(Y, (M ?? 1) - 1, D ?? 1, h ?? 0, m ?? 0, s ?? 0);

  // Subtract the zone's offset AT that candidate to get the real UTC instant.
  // We iterate once more to cover DST transitions: the offset at `t` can
  // differ from the offset at `candidateMs`.
  const offset1 = getTzOffsetMs(candidateMs, tz);
  let instantMs = candidateMs - offset1;
  const offset2 = getTzOffsetMs(instantMs, tz);
  if (offset2 !== offset1) instantMs = candidateMs - offset2;

  return new Date(instantMs).toISOString();
}

// ─── UTC instant → wall clock in a given zone ────────────────────────────────

export interface ZonedParts {
  date: string; // YYYY-MM-DD
  time: string; // HH:MM  (24h, zero-padded)
}

/**
 * Inverse of `zonedWallToUtc`: given a UTC instant and a display zone, return
 * the wall-clock `YYYY-MM-DD` + `HH:MM` that zone sees.
 *
 * Used both for display and for pre-populating the edit form in the event's
 * own authored zone (so editing an NY-authored event from a London laptop
 * still shows the original "9:00 AM" — not "2:00 PM" — by default).
 */
export function utcToZonedParts(utcIso: string, tz: string): ZonedParts {
  const d = new Date(utcIso);
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    year:   "numeric",
    month:  "2-digit",
    day:    "2-digit",
    hour:   "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const g = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((p) => p.type === type)?.value ?? "00";

  // Chromium quirk: hour can come back as "24" at midnight in some locales.
  let hour = g("hour");
  if (hour === "24") hour = "00";

  return {
    date: `${g("year")}-${g("month")}-${g("day")}`,
    time: `${hour}:${g("minute")}`,
  };
}

// ─── Display formatters ──────────────────────────────────────────────────────

/**
 * Format the time-of-day portion of a UTC instant in a specific zone.
 * Defaults to `"9:00 AM"` style.
 */
export function formatZonedTime(
  utcIso: string | null | undefined,
  tz: string,
  opts: Intl.DateTimeFormatOptions = { hour: "numeric", minute: "2-digit" },
): string {
  if (!utcIso) return "";
  return new Intl.DateTimeFormat(undefined, { timeZone: tz, ...opts })
    .format(new Date(utcIso));
}

/**
 * Format the date portion of a UTC instant in a specific zone.
 * Defaults to `"Tue, Apr 21"` style.
 */
export function formatZonedDate(
  utcIso: string | null | undefined,
  tz: string,
  opts: Intl.DateTimeFormatOptions = {
    weekday: "short",
    month:   "short",
    day:     "numeric",
  },
): string {
  if (!utcIso) return "";
  return new Intl.DateTimeFormat(undefined, { timeZone: tz, ...opts })
    .format(new Date(utcIso));
}

/**
 * Return the short timezone abbreviation (e.g. `"EST"`, `"GMT"`) that the
 * given zone displays for the given instant. Used in the "9:00 AM EST (your
 * time: 2:00 PM GMT)" subtitle.
 *
 * Abbreviations are locale-sensitive — `Intl` may return `"GMT-5"` instead
 * of `"EST"` on some Linux ICU builds. This helper is best-effort: the UI
 * should still be readable without it.
 */
export function getTzAbbrev(tz: string, utcIso?: string | null): string {
  const d = utcIso ? new Date(utcIso) : new Date();
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone:     tz,
      timeZoneName: "short",
    }).formatToParts(d);
    return parts.find((p) => p.type === "timeZoneName")?.value ?? tz;
  } catch {
    return tz;
  }
}

// ─── Internal: zone offset lookup ────────────────────────────────────────────

/**
 * Returns `+N ms` east of UTC for the given IANA zone at the given instant.
 * Positive for Tokyo, negative for New York. Used by `zonedWallToUtc`.
 */
function getTzOffsetMs(utcMs: number, tz: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    year:   "numeric",
    month:  "2-digit",
    day:    "2-digit",
    hour:   "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(new Date(utcMs));

  const get = (t: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((p) => p.type === t)?.value ?? 0);

  let hour = get("hour");
  if (hour === 24) hour = 0; // Chromium midnight quirk

  const asIfUtcMs = Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    hour,
    get("minute"),
    get("second"),
  );
  return asIfUtcMs - utcMs;
}
